// Jedyne źródło formatowania liczb, pieniędzy, procentów i dat w panelu.
//
// Audyt UI zastał 105 ręcznych `toFixed(2)` obok 10 użyć Intl, 97 razy "PLN"
// obok 14 razy "zł" i 33 twarde `toLocaleDateString('pl-PL')` — w efekcie ta
// sama kwota wyglądała raz jak `1234.50`, raz jak `1 234,50 zł`, a data
// ignorowała wybrany język. Formatowanie robi tu Intl, więc zapis waluty i
// separatory biorą się z locale, a nie z ręcznych sklejeń.
//
// Moduł jest CZYSTY: bez Reacta, bez axiosa, bez localStorage. Locale ZAWSZE
// przychodzi argumentem (strony podają `currentLanguage` z useTranslation()),
// bo tylko wtedy da się je przetestować i tylko wtedy zmiana języka odświeża
// widok przez normalny re-render.

const FALLBACK = '—';

// Kody używane w aplikacji ('pl' | 'en' | 'de', patrz translations.js) na tagi
// BCP47 dla Intl. Angielski celowo 'en-GB', nie 'en-US': klienci są europejscy,
// więc data ma być dzień/miesiąc/rok, a nie miesiąc/dzień/rok.
const LOCALE_TAGS = {
  pl: 'pl-PL',
  en: 'en-GB',
  de: 'de-DE',
};

const DEFAULT_TAG = LOCALE_TAGS.pl;

// Format dla react-datepicker (biblioteka używa wzorców date-fns, nie Intl).
// Strony podawały tam 'yyyy-MM-dd', mimo że reszta panelu pokazuje dd.mm.rrrr.
export const DATEPICKER_FORMAT = 'dd.MM.yyyy';

/**
 * Kod języka aplikacji → tag BCP47. Przyjmuje też gotowy tag ('pl-PL') i
 * warianty ('en-US'), bo część miejsc trzyma język w localStorage bez kontroli
 * kształtu. Nieznane / puste → polski, żeby nigdy nie polecieć wyjątkiem z Intl.
 */
export function localeTag(locale) {
  if (typeof locale !== 'string') return DEFAULT_TAG;
  const base = locale.trim().toLowerCase().split(/[-_]/)[0];
  return LOCALE_TAGS[base] || DEFAULT_TAG;
}

// Formattery Intl są drogie w konstrukcji, a listy (magazyn, faktury, kalendarz)
// wołają format setki razy na render — stąd cache per zestaw opcji.
const numberFormatterCache = new Map();
function numberFormatter(tag, opts) {
  const key = `${tag}|${JSON.stringify(opts)}`;
  let f = numberFormatterCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(tag, opts);
    numberFormatterCache.set(key, f);
  }
  return f;
}

const dateFormatterCache = new Map();
function dateFormatter(tag, opts) {
  const key = `${tag}|${JSON.stringify(opts)}`;
  let f = dateFormatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(tag, opts);
    dateFormatterCache.set(key, f);
  }
  return f;
}

/**
 * Liczba z dowolnego wejścia, jakie potrafi przyjść z backendu albo formularza,
 * albo `null` gdy wartości nie ma / jest bez sensu. `null`, a nie 0 — bo 0 to
 * poprawna kwota i musi się wyświetlić jako "0,00 zł", nie jako myślnik.
 */
export function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;

  // Zdejmujemy spacje (także niełamiące, którymi Intl grupuje tysiące — dzięki
  // temu round-trip format→parse działa) oraz sufiksy walutowe/procent, bo
  // wartości bywają wklejane wprost z UI.
  let s = value.replace(/[\s\u00a0\u202f\u2009]/g, '');
  s = s.replace(/(zł|PLN|EUR|USD|€|\$|%)/gi, '');
  if (!s) return null;

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && hasDot) {
    // Ostatni separator jest dziesiętny ("1.234,50" i "1,234.50"), pozostałe to
    // grupowanie tysięcy.
    const decimalSep = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.';
    const groupSep = decimalSep === ',' ? '.' : ',';
    s = s.split(groupSep).join('').replace(decimalSep, '.');
  } else if (hasComma) {
    // Jeden przecinek = polski separator dziesiętny ("12,50" z formularza).
    // Kilka przecinków nie może być dziesiętnych, więc to grupowanie.
    s = s.split(',').length === 2 ? s.replace(',', '.') : s.split(',').join('');
  } else if (hasDot && s.split('.').length > 2) {
    s = s.split('.').join('');
  }

  // Number('') === 0, a Number('12abc') === NaN — jawny regex odsiewa oba
  // przypadki, zanim zamienią się w fałszywe zero na fakturze.
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Data z dowolnego wejścia albo `null`. Klucz: string `YYYY-MM-DD` parsujemy
 * ręcznie jako datę LOKALNĄ. `new Date('2026-08-17')` to według specyfikacji
 * północ UTC, więc w strefie ujemnej wyświetliłoby się 16.08 — a takie stringi
 * (daty rezerwacji, terminy płatności) przychodzą z backendu jako czysty dzień,
 * bez pory doby.
 */
export function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return Number.isFinite(value) ? new Date(value) : null;
  if (typeof value !== 'string') return null;

  const s = value.trim();
  if (!s) return null;

  const dayOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dayOnly) {
    const [, y, m, d] = dayOnly;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    // Data typu 2026-02-31 „przewija się" na marzec — round-trip to wykrywa i
    // odrzuca, zamiast pokazywać cichą podmianę dnia.
    if (date.getFullYear() !== Number(y)
      || date.getMonth() !== Number(m) - 1
      || date.getDate() !== Number(d)) return null;
    return date;
  }

  // Postgres oddaje czasem "2026-08-17 14:30:00" (spacja zamiast T) — bez
  // normalizacji część silników parsuje to jako Invalid Date.
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s) ? s.replace(' ', 'T') : s;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Kwota z symbolem waluty. `style:'currency'` sprawia, że to Intl decyduje o
 * zapisie ("1 234,50 zł" po polsku, "PLN 1,234.50" po angielsku), więc rozjazd
 * "zł" vs "PLN" znika z kodu stron.
 * @param {*} value dowolne wejście (number, string z przecinkiem, null…)
 * @param {{locale?:string, currency?:string, fallback?:string, decimals?:number}} [options]
 */
export function formatMoney(value, options = {}) {
  const { locale, currency = 'PLN', fallback = FALLBACK, decimals = 2 } = options;
  const n = toNumber(value);
  if (n === null) return fallback;
  return numberFormatter(localeTag(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/**
 * Zwykła liczba (sztuki, dni, liczniki) z separatorem tysięcy właściwym dla
 * języka. Domyślnie bez części dziesiętnej, bo większość liczników jest całkowita.
 * @param {{locale?:string, decimals?:number, fallback?:string}} [options]
 */
export function formatNumber(value, options = {}) {
  const { locale, decimals = 0, fallback = FALLBACK } = options;
  const n = toNumber(value);
  if (n === null) return fallback;
  return numberFormatter(localeTag(locale), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/**
 * Procent. Wejście to punkty procentowe (25 → "25%"), bo tak trzyma je backend
 * (utilization, rabaty, stawki VAT); ułamek (0.25) przepuszcza `asFraction:true`.
 * Znak procentu stawia Intl, więc niemieckie "25 %" ze spacją wychodzi samo.
 * @param {{locale?:string, decimals?:number, fallback?:string, asFraction?:boolean}} [options]
 */
export function formatPercent(value, options = {}) {
  const { locale, decimals = 0, fallback = FALLBACK, asFraction = false } = options;
  const n = toNumber(value);
  if (n === null) return fallback;
  return numberFormatter(localeTag(locale), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(asFraction ? n : n / 100);
}

/** Strefa, w której serwer liczy dni firmy (`lib/businessDate.js`). */
export const STREFA_FIRMY = 'Europe/Warsaw';

/**
 * Sama data, np. "17.08.2026". `2-digit` dla dnia i miesiąca jest celowe: w
 * listach i tabelach kolumna dat musi mieć stałą szerokość.
 * `timeZone` tylko dla znaczników czasu, których dzień liczy serwer w strefie
 * firmy (np. początek przerwy o północy w Warszawie). Daty bez godziny
 * (`RRRR-MM-DD`) są czytane jako północ czasu lokalnego, więc z `timeZone`
 * mogłyby przeskoczyć na sąsiedni dzień.
 * @param {{locale?:string, fallback?:string, timeZone?:string}} [options]
 */
export function formatDate(value, options = {}) {
  const { locale, fallback = FALLBACK, timeZone } = options;
  const d = toDate(value);
  if (!d) return fallback;
  return dateFormatter(localeTag(locale), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(d);
}

/**
 * Data z godziną, np. "17.08.2026, 14:30". Zawsze zegar 24-godzinny — panel jest
 * europejski, a jawne `hour12:false` chroni przed wariantami locale, które
 * wstawiłyby AM/PM.
 * @param {{locale?:string, fallback?:string}} [options]
 */
export function formatDateTime(value, options = {}) {
  const { locale, fallback = FALLBACK } = options;
  const d = toDate(value);
  if (!d) return fallback;
  return dateFormatter(localeTag(locale), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}

/**
 * Data słownie z dniem tygodnia, np. "sobota, 10 października 2026". Do nagłówków
 * i wydruków, nie do tabel: szerokość zależy od miesiąca i języka.
 * @param {{locale?:string, fallback?:string}} [options]
 */
export function formatLongDate(value, options = {}) {
  const { locale, fallback = FALLBACK } = options;
  const d = toDate(value);
  if (!d) return fallback;
  return dateFormatter(localeTag(locale), {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }).format(d);
}

/**
 * Zakres dat rezerwacji, np. "17.08.2026 – 20.08.2026". Ten sam dzień po obu
 * stronach daje JEDNĄ datę (wypożyczenie jednodniowe nie ma się czytać jako
 * "17.08.2026 – 17.08.2026"). Gdy jedna ze stron jest pusta/niepoprawna,
 * zwracamy tę drugą — lepiej pokazać znaną połowę niż myślnik zamiast obu.
 * @param {{locale?:string, fallback?:string, separator?:string}} [options]
 */
export function formatDateRange(from, to, options = {}) {
  const { locale, fallback = FALLBACK, separator = ' – ' } = options;
  const dFrom = toDate(from);
  const dTo = toDate(to);
  if (!dFrom && !dTo) return fallback;
  if (!dFrom || !dTo) return formatDate(dFrom || dTo, { locale, fallback });

  const a = formatDate(dFrom, { locale, fallback });
  const b = formatDate(dTo, { locale, fallback });
  // Porównanie po sformatowanym tekście, a nie po timestampie: dla użytkownika
  // „ten sam dzień" znaczy tyle, co identyczny napis, niezależnie od godziny.
  return a === b ? a : `${a}${separator}${b}`;
}
